import { useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Gauge,
  HeartPulse,
  Pill,
  Target,
  TriangleAlert,
} from "lucide-react";
import {
  buildPsyLogTimeline,
  groupTimelineByDay,
  summarizeTimeline,
  type PsyLogEventType,
  type PsyLogTimelineEvent,
} from "@/lib/clinical/psylogTimeline";
import { PSYLOG_TIMEZONE } from "@/lib/clinical/psylogNative";

const TYPE_META: Record<PsyLogEventType, { label: string; icon: any }> = {
  dose: { label: "Dose", icon: Pill },
  symptom: { label: "Sintoma", icon: Target },
  checkin: { label: "Check-in", icon: CheckCircle2 },
  session: { label: "Sessão", icon: CalendarClock },
  substance_use: { label: "Uso não prescrito", icon: TriangleAlert },
  psylog: { label: "PsyLog", icon: ClipboardList },
  vital: { label: "Sinais vitais", icon: HeartPulse },
};

function fmtDate(value: string) {
  return new Date(value).toLocaleDateString("pt-BR", { timeZone: PSYLOG_TIMEZONE, weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtTime(value: string) {
  return new Date(value).toLocaleTimeString("pt-BR", { timeZone: PSYLOG_TIMEZONE, hour: "2-digit", minute: "2-digit" });
}

function eventValue(event: PsyLogTimelineEvent, key: string) {
  const parsed = Number(event.source?.[key]);
  return Number.isFinite(parsed) ? parsed : null;
}

function average(values: Array<number | null>) {
  const valid = values.filter((value): value is number => value != null);
  if (!valid.length) return null;
  return Math.round(valid.reduce((sum, value) => sum + value, 0) / valid.length);
}

function dayStats(events: PsyLogTimelineEvent[]) {
  const doses = events.filter((event) => event.type === "dose");
  return {
    doses: doses.length,
    redoses: doses.filter((event) => /redose|sobreposicao|extra|problem/i.test(String(event.source?.log_type ?? ""))).length,
    symptoms: events.filter((event) => event.type === "symptom").length,
    checkins: events.filter((event) => event.type === "checkin").length,
    vitals: events.filter((event) => event.type === "vital").length,
    sessions: events.filter((event) => event.type === "session").length,
    focus: average(doses.map((event) => eventValue(event, "focus_0_100"))),
    anxiety: average(doses.map((event) => eventValue(event, "anxiety_0_100"))),
    benefit: average(doses.map((event) => eventValue(event, "benefit_0_100"))),
    adverse: average(doses.map((event) => eventValue(event, "adverse_0_100"))),
  };
}

export function PsyLogTimelinePanel({
  doses = [],
  symptomMeasurements = [],
  checkins = [],
  sessions = [],
  substanceUse = [],
  psylogEvents = [],
  vitalSigns = [],
}: {
  doses?: any[];
  symptomMeasurements?: any[];
  checkins?: any[];
  sessions?: any[];
  substanceUse?: any[];
  psylogEvents?: any[];
  vitalSigns?: any[];
}) {
  const events = useMemo(
    () => buildPsyLogTimeline({ doses, symptomMeasurements, checkins, sessions, substanceUse, psylogEvents, vitalSigns }),
    [doses, symptomMeasurements, checkins, sessions, substanceUse, psylogEvents, vitalSigns],
  );
  const summary = useMemo(() => summarizeTimeline(events), [events]);
  const [enabled, setEnabled] = useState<Record<PsyLogEventType, boolean>>({ dose: true, symptom: true, checkin: true, session: true, substance_use: true, psylog: true, vital: true });
  const [selected, setSelected] = useState<PsyLogTimelineEvent | null>(null);
  const visibleEvents = events.filter((event) => enabled[event.type]);
  const visibleGroups = groupTimelineByDay(visibleEvents);
  const dailyRows = visibleGroups.map((group) => ({ date: group.date, ...dayStats(group.events) }));

  if (!events.length) {
    return <Card className="p-5 text-sm text-muted-foreground">Sem eventos datados. Registre dose, sintoma, check-in, PA, sessão ou nota com data e hora reais.</Card>;
  }

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-semibold flex items-center gap-2"><Activity className="h-4 w-4" />PsyLog clínico</div>
            <div className="text-xs text-muted-foreground mt-0.5">Linha temporal por timestamp real em São Paulo. Dias ficam recolhidos para reduzir poluição sem ocultar dados.</div>
          </div>
          <div className="flex gap-2"><Badge variant="outline">{summary.dated} eventos</Badge>{summary.redoses ? <Badge variant="destructive">{summary.redoses} redose/sobreposição</Badge> : null}</div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(TYPE_META) as PsyLogEventType[]).map((type) => {
            const meta = TYPE_META[type];
            const count = summary.counts[type] ?? 0;
            return (
              <Button key={type} size="sm" className="h-7 px-2 text-xs" variant={enabled[type] ? "secondary" : "ghost"} onClick={() => setEnabled((current) => ({ ...current, [type]: !current[type] }))}>
                <meta.icon className="h-3.5 w-3.5 mr-1" />{meta.label}<span className="ml-1 opacity-60">{count}</span>
              </Button>
            );
          })}
        </div>
      </div>

      <Tabs defaultValue="timeline" className="p-4">
        <TabsList className="h-9"><TabsTrigger value="timeline">Linha do tempo</TabsTrigger><TabsTrigger value="days">Comparar dias</TabsTrigger><TabsTrigger value="matrix">Matriz</TabsTrigger></TabsList>

        <TabsContent value="timeline" className="mt-4 grid xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
          <div className="space-y-2 min-w-0">
            {visibleGroups.map((group, groupIndex) => {
              const stats = dayStats(group.events);
              return (
                <details key={group.date} open={groupIndex < 2} className="rounded-lg border bg-card group">
                  <summary className="cursor-pointer list-none p-3 flex items-center gap-3">
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm">{fmtDate(group.events[0].at)}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{group.events.length} eventos · {stats.doses} doses · {stats.redoses} redose/sobreposição · {stats.vitals} PA</div>
                    </div>
                    <div className="hidden sm:flex gap-1"><Badge variant="outline" className="text-[9px]">{fmtTime(group.events.at(-1)!.at)}–{fmtTime(group.events[0].at)}</Badge>{stats.redoses ? <Badge variant="destructive" className="text-[9px]">revisar</Badge> : null}</div>
                  </summary>

                  <div className="border-t divide-y">
                    {group.events.map((event) => {
                      const meta = TYPE_META[event.type];
                      return (
                        <button key={event.id} type="button" onClick={() => setSelected(event)} className={`w-full text-left p-3 transition-colors hover:bg-muted/30 ${selected?.id === event.id ? "bg-primary/5" : ""}`}>
                          <div className="grid grid-cols-[50px_22px_minmax(0,1fr)_auto] items-start gap-2">
                            <div className="font-mono text-[11px] text-muted-foreground pt-0.5">{fmtTime(event.at)}</div>
                            <meta.icon className="h-4 w-4 mt-0.5" />
                            <div className="min-w-0"><div className="font-medium text-sm">{event.title}</div><div className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-wrap break-words">{event.detail || "sem descrição adicional"}</div></div>
                            <div className="flex flex-col items-end gap-1"><Badge variant="outline" className="text-[9px]">{meta.label}</Badge>{event.intensity != null ? <Badge variant="secondary" className="text-[9px]">{Math.round(event.intensity)}/100</Badge> : null}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>

          <div className="xl:sticky xl:top-20">
            {selected ? (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex flex-wrap justify-between gap-2"><div className="font-medium">{selected.title}</div><Badge variant="outline">{fmtDate(selected.at)} · {fmtTime(selected.at)}</Badge></div>
                <div className="text-sm whitespace-pre-wrap break-words">{selected.detail || "Sem detalhe adicional."}</div>
                {selected.tags.length ? <div className="flex flex-wrap gap-1">{selected.tags.map((tag) => <Badge key={tag} variant="secondary" className="text-[9px]">{tag}</Badge>)}</div> : null}
                <div className="text-xs text-muted-foreground">Associação temporal, não prova causal. Cruze com fase da curva, sono, cafeína, alimento, formulação e medidas adjacentes.</div>
              </div>
            ) : <div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">Selecione um evento para fixar sua leitura clínica sem perder o ponto da linha temporal.</div>}
          </div>
        </TabsContent>

        <TabsContent value="days" className="mt-4">
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs min-w-[820px]">
              <thead className="bg-muted/40"><tr>{["Dia", "Doses", "Redose/sobrep.", "Sintomas", "Check-ins", "PA", "Sessões", "Foco", "Ansiedade", "Benefício", "Adverso"].map((label) => <th key={label} className="text-left px-3 py-2 font-medium">{label}</th>)}</tr></thead>
              <tbody>{dailyRows.map((row) => <tr key={row.date} className="border-t"><td className="px-3 py-2 font-medium">{row.date.split("-").reverse().join("/")}</td><td className="px-3 py-2">{row.doses}</td><td className="px-3 py-2">{row.redoses}</td><td className="px-3 py-2">{row.symptoms}</td><td className="px-3 py-2">{row.checkins}</td><td className="px-3 py-2">{row.vitals}</td><td className="px-3 py-2">{row.sessions}</td><td className="px-3 py-2">{row.focus ?? "—"}</td><td className="px-3 py-2">{row.anxiety ?? "—"}</td><td className="px-3 py-2">{row.benefit ?? "—"}</td><td className="px-3 py-2">{row.adverse ?? "—"}</td></tr>)}</tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="matrix" className="mt-4 overflow-x-auto">
          <div className="grid gap-2 min-w-[680px]">
            {dailyRows.map((row) => (
              <div key={row.date} className="grid grid-cols-[100px_repeat(6,minmax(70px,1fr))] gap-1 text-xs items-stretch">
                <div className="rounded-md border p-2 font-medium">{row.date.split("-").slice(1).reverse().join("/")}</div>
                {[["Dose", row.doses], ["Redose", row.redoses], ["Sintoma", row.symptoms], ["Check-in", row.checkins], ["PA", row.vitals], ["Sessão", row.sessions]].map(([label, value]) => <div key={String(label)} className="rounded-md border p-2 text-center"><div className="text-[9px] uppercase text-muted-foreground">{label}</div><div className="text-lg font-semibold">{value}</div></div>)}
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" />Ausência de célula é ausência de registro, não ausência de sintoma.</div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
